import styled, { CSSObject } from '@emotion/styled'
import { format, parseISO } from 'date-fns'
import { MdOutlineDiscount } from 'react-icons/md'
import { ellipsis } from 'shop/components/Cart/commonStyles'
import {
  DiscountType,
  Prompt as PromptType,
  PromptAppliesToType,
  PromptRestrictionType
} from 'shop/components/Landing'
import { formatMoney } from 'shop/utils/common'

interface PromptProps {
  prompt: PromptType
  hasMultiplePrompts: boolean
}

const generatePromptContent = (
  prompt: PromptType
): { title: string; restrictions: string } => {
  let title = ''

  switch (prompt.discountType) {
    case DiscountType.PERCENTAGE:
      title += `${prompt.amount}% off`
      break
    case DiscountType.FIXED:
      title += `${formatMoney(prompt.amount, { stripTrailingZeros: true })} off`
      break
    case DiscountType.BUY_ONE_SPECIAL:
      if (prompt.amount === '100') {
        title += `Buy 1, get 1 free on`
      } else if (prompt.amount === '50') {
        title += `Buy 1, get 1 half price on`
      } else {
        title += `${prompt.amount}% off 2nd item on`
      }
      break
  }
  switch (prompt.appliesTo) {
    case PromptAppliesToType.ALL_PRODUCTS:
      title += ` all products`
      break
    case PromptAppliesToType.SPECIFIC_PRODUCTS:
      title += ` selected products`
      break
    case PromptAppliesToType.ORDER:
      title += ` your order`
      break
    case PromptAppliesToType.DELIVERY:
      title += ` your delivery`
      break
  }

  const restrictions = prompt.restrictions
    .reduce<string[]>((acc, restriction) => {
      if (restriction.type === PromptRestrictionType.MINIMUM_ORDER_VALUE) {
        return [
          ...acc,
          `${formatMoney(restriction.amount, { stripTrailingZeros: true })} minimum spend`
        ]
      } else if (
        restriction.type === PromptRestrictionType.MAX_USES_PER_CUSTOMER
      ) {
        return [...acc, 'Account required']
      } else if (restriction.type === PromptRestrictionType.EXPIRY) {
        return [
          ...acc,
          `Valid until ${format(parseISO(restriction.endDate), 'dd/MM/yyyy')}`
        ]
      } else if (restriction.type === PromptRestrictionType.MAXIMUM_VALUE) {
        return [
          ...acc,
          `Max value of ${formatMoney(restriction.amount, { stripTrailingZeros: true })}`
        ]
      } else if (restriction.type === PromptRestrictionType.QUANTITY_LIMIT) {
        return [...acc, `Max ${restriction.limit} items per order`]
      }
      return acc
    }, [])
    .join(' • ')

  return { title, restrictions }
}

const ShopPrompt = ({ prompt, hasMultiplePrompts }: PromptProps) => {
  const { title, restrictions } = generatePromptContent(prompt)
  const promptDetails = !!restrictions.length
    ? restrictions
    : 'Your Discount will be automatically applied to your cart'

  return (
    <Container hasMultiplePrompts={hasMultiplePrompts}>
      <TextContent>
        <Title>{title}</Title>
        <Description>{prompt.description}</Description>
        {<Details>{promptDetails}</Details>}
      </TextContent>
      <ImageContent>
        <Image>
          <Symbol>
            {prompt.discountType === DiscountType.BUY_ONE_SPECIAL ? (
              <MdOutlineDiscount size={24} />
            ) : prompt.discountType === DiscountType.PERCENTAGE ? (
              '%'
            ) : (
              '£'
            )}
          </Symbol>
        </Image>
      </ImageContent>
    </Container>
  )
}

const Container = styled.div<{ hasMultiplePrompts: boolean }>(
  ({ theme, hasMultiplePrompts }) => ({
    backgroundColor: 'white',
    border: '1px solid #D9D9D9',
    borderRadius: theme.borderRadius,
    padding: '12px',
    flex: '1 0 95%',
    display: 'flex',
    scrollSnapAlign: 'center',
    [theme.mediaQueries.viewport7]: {
      flex: hasMultiplePrompts ? '1 0 calc(50% - 4px)' : '1 1 0',
      scrollSnapAlign: 'center'
    }
  })
)

const TextContent = styled.div(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    justifyContent: 'space-between',
    height: 'auto'
  })
)

const ImageContent = styled.div(
  ({ theme }): CSSObject => ({
    width: '82px',
    height: '72px',
    border: '1px solid #D9D9D9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#edf8eb',
    borderRadius: theme.borderRadius
  })
)

const Title = styled.p(() => ({
  fontSize: '14px',
  fontWeight: 500,
  margin: '0 0 2px 0'
}))

const Image = styled.div(
  (): CSSObject => ({
    width: '44px',
    height: '44px',
    backgroundColor: '#dff0dd',
    clipPath:
      'path("M18.2155 1.97743C19.9913 -0.659141 24.0062 -0.659142 25.782 1.97742C26.941 3.6981 29.1967 4.40233 31.187 3.66478C34.2365 2.53464 37.4847 4.8021 37.3084 7.93801C37.1934 9.98457 38.5875 11.8283 40.6488 12.3556C43.8074 13.1635 45.048 16.8323 42.987 19.2698C41.642 20.8605 41.642 23.1395 42.987 24.7302C45.048 27.1677 43.8074 30.8365 40.6488 31.6444C38.5875 32.1717 37.1934 34.0154 37.3084 36.062C37.4847 39.1979 34.2365 41.4654 31.187 40.3352C29.1967 39.5977 26.941 40.3019 25.782 42.0226C24.0062 44.6591 19.9913 44.6591 18.2155 42.0226C17.0566 40.3019 14.8008 39.5977 12.8106 40.3352C9.76102 41.4654 6.51287 39.1979 6.68915 36.062C6.80419 34.0154 5.41004 32.1717 3.34872 31.6444C0.190195 30.8365 -1.05049 27.1677 1.01054 24.7302C2.35561 23.1395 2.35561 20.8605 1.01054 19.2698C-1.05049 16.8323 0.190195 13.1635 3.34873 12.3556C5.41004 11.8283 6.80419 9.98457 6.68915 7.93801C6.51287 4.8021 9.76101 2.53464 12.8106 3.66478C14.8008 4.40233 17.0566 3.6981 18.2155 1.97743Z")',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  })
)

const Symbol = styled.span(
  (): CSSObject => ({
    fontSize: '24px',
    color: '#389E0D',
    fontWeight: 800,
    '> svg': {
      display: 'block'
    }
  })
)

const Description = styled.p(
  (): CSSObject => ({
    fontSize: '14px',
    color: '#59595A',
    margin: '0 0 6px 0',
    width: '90%',
    ...ellipsis,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical'
  })
)

const Details = styled.p(() => ({
  fontSize: '12px',
  color: '#59595A',
  margin: 'auto 0 0 0',
  width: '90%'
}))

export default ShopPrompt
